/**
 * Theme store for dark/light mode toggle
 * Enhanced with config-driven theme support
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ThemeDefinition } from '../types/theme';
import { applyThemeVariables } from '../utils/theme';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  themeConfig: ThemeDefinition | null;
  setTheme: (theme: Theme) => void;
  setThemeConfig: (config: ThemeDefinition) => void;
  getEffectiveTheme: () => 'light' | 'dark';
  getThemeConfig: () => ThemeDefinition | null;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      themeConfig: null,
      
      setTheme: (theme) => {
        console.log('[Theme] Setting theme to:', theme);
        set({ theme });
        // Apply theme immediately - use setTimeout to ensure state is updated
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            const effectiveTheme = theme === 'system' 
              ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
              : theme;
            console.log('[Theme] Effective theme:', effectiveTheme);
            applyTheme(effectiveTheme);
            
            // Also apply CSS variables if theme config is available
            const config = get().themeConfig;
            if (config) {
              applyThemeVariables(config);
            }
          }, 0);
        }
      },
      
      setThemeConfig: (config) => {
        console.log('[Theme] Setting theme config:', config.id);
        set({ themeConfig: config });
        // Apply CSS variables immediately
        if (typeof window !== 'undefined') {
          applyThemeVariables(config);
        }
      },
      
      getEffectiveTheme: () => {
        const { theme } = get();
        if (typeof window === 'undefined') return 'light';
        if (theme === 'system') {
          return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return theme;
      },
      
      getThemeConfig: () => {
        return get().themeConfig;
      },
    }),
    {
      name: 'tracekit-theme',
      onRehydrateStorage: () => (state) => {
        // Apply theme after rehydration
        if (state && typeof window !== 'undefined') {
          setTimeout(() => {
            const effectiveTheme = state.getEffectiveTheme();
            applyTheme(effectiveTheme);
            
            // Apply CSS variables if available
            const config = state.getThemeConfig();
            if (config) {
              applyThemeVariables(config);
            }
          }, 0);
        }
      },
    }
  )
);

// Apply theme to document
export function applyTheme(theme: 'light' | 'dark') {
  if (typeof window === 'undefined') return;
  
  // Get documentElement (this is the <html> element)
  const root = document.documentElement;
  
  // Remove dark class completely first
  root.classList.remove('dark');
  
  // Add dark class only if theme is dark
  if (theme === 'dark') {
    root.classList.add('dark');
  }
  
  // Force a reflow to ensure styles apply immediately
  void root.offsetHeight;
  
  // Double-check and log
  const hasDark = root.classList.contains('dark');
  console.log('[Theme] Applied theme:', theme, 'Dark class present:', hasDark, 'Expected:', theme === 'dark');
  
  // If there's a mismatch, force it
  if (theme === 'dark' && !hasDark) {
    root.classList.add('dark');
    console.log('[Theme] Force-added dark class');
  } else if (theme === 'light' && hasDark) {
    root.classList.remove('dark');
    console.log('[Theme] Force-removed dark class');
  }
}

// Initialize theme on load (runs once when module loads)
if (typeof window !== 'undefined') {
  // Function to initialize theme
  const initializeTheme = () => {
    const store = useThemeStore.getState();
    if (store) {
      const effectiveTheme = store.getEffectiveTheme();
      applyTheme(effectiveTheme);
    }
  };
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTheme);
  } else {
    // DOM already ready - use setTimeout to ensure store is hydrated
    setTimeout(initializeTheme, 0);
  }
  
  // Listen for system theme changes when using 'system' mode
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleSystemThemeChange = (e: MediaQueryListEvent | MediaQueryList) => {
    const currentTheme = useThemeStore.getState().theme;
    if (currentTheme === 'system') {
      const isDark = 'matches' in e ? e.matches : mediaQuery.matches;
      applyTheme(isDark ? 'dark' : 'light');
    }
  };
  
  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleSystemThemeChange);
  } else {
    // Fallback for older browsers
    mediaQuery.addListener(handleSystemThemeChange as any);
  }
  
  // Also subscribe to store changes to handle theme updates
  useThemeStore.subscribe((state) => {
    if (state.theme) {
      const effectiveTheme = state.getEffectiveTheme();
      applyTheme(effectiveTheme);
    }
  });
}

